<template>
  <AdminLayout>
    <KitchenSink />
    <Head title="Design System" />

    <div class="flex flex-col gap-y-16 text-gray-800 pb-52">
      <div class="flex flex-col gap-y-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        <div v-for="size in fontSizes">
          <p
            class="flex justify-between items-center"
            :class="`text-${size}`"
          >
            Font size {{ size }}
            <span class="text-sm">{{ fontSizeToPx(size) }}</span>
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-y-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        <div v-for="(weight, weightName) in reverseObjectProperties(tailwindConfig.theme.fontWeight)">
          <p
            class="flex justify-between items-center text-base"
            :class="`font-${weightName}`"
          >
            Font weight: {{ weightName }}
            <span class="text-sm">{{ weight }}</span>
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-y-12 gap-x-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        <div v-for="(colorCategory, colorCategoryName) in tailwindConfig.theme.colors">
          <p class="text-xl uppercase font-bold py-2">{{ colorCategoryName }}</p>
          <div class="flex gap-x-6 gap-y-6 flex-wrap">
            <div
              v-if="typeof colorCategory !== 'string'"
              v-for="(_, colorName) in colorCategory"
            >
              <div
                class="w-24 h-24 rounded border"
                :class="getColor(colorCategoryName, colorName)"
              ></div>
              <span class="text-sm">{{ `${colorCategoryName}-${colorName}` }}</span>
            </div>
            <div v-else>
              <div
                class="w-24 h-24 rounded border"
                :class="getColor(colorCategoryName)"
              ></div>
              <span class="text-sm">{{ colorCategoryName }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap gap-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        <button class="btn btn-primary">Button Primary</button>
        <button class="btn btn-secondary">Button Secondary</button>
        <button class="btn btn-secondary-outline">Button Secondary Outline</button>
        <button class="btn btn-danger">Button Danger</button>
        <button class="btn btn-success">Button Success</button>
      </div>

      <div class="flex flex-col gap-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        <div
          v-for="color in basicBadge.colors"
          class="flex flex-wrap gap-6"
        >
          <div v-for="size in basicBadge.sizes">
            <BasicBadge
              :color="color"
              :size="size"
            >
              <span>Basic Badge {{ color }} / {{ size }}</span>
            </BasicBadge>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        {{ multiSelectModel }}
        <MultiSelectInput
          v-model="multiSelectModel"
          :options="[
            { label: 'Option 1', value: 1 },
            { label: 'Option 2', value: 2 },
            { label: 'Option 3', value: 3 }
          ]"
          placeholder="Nothing Selected..."
        >
          Select an Option
        </MultiSelectInput>
      </div>

      <div class="flex flex-wrap gap-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        <button
          @click="openInfoConfirm"
          class="btn btn-primary"
        >
          Open Confirm Modal - Information
        </button>
        <button
          @click="openDangerConfirm"
          class="btn btn-danger"
        >
          Open Confirm Modal - Danger
        </button>
      </div>

      <div class="flex flex-col gap-6 bg-gray-100 px-6 lg:px-24 rounded p-6">
        <button
          @click="openSmallModal"
          class="btn btn-primary"
        >
          Small Modal
        </button>
        <button
          @click="openMediumModal"
          class="btn btn-primary"
        >
          Medium Modal
        </button>
        <button
          @click="openLargeModal"
          class="btn btn-primary"
        >
          Large Modal
        </button>

        <SmallModal
          :is-open="smallModalOpen"
          @close="closeSmallModal"
        >
          <template #header>Modal Header</template>
          <p>This is medium modal</p>
          <template #footer>Modal Footer</template>
        </SmallModal>
        <MediumModal
          :is-open="mediumModalOpen"
          @close="closeMediumModal"
        >
          <template #header>Modal Header</template>
          <p>This is medium modal</p>
          <template #footer>Modal Footer</template>
        </MediumModal>
        <LargeModal
          :is-open="largeModalOpen"
          @close="closeLargeModal"
        >
          <template #header>Modal Header</template>
          <p>This is medium modal</p>
          <template #footer>Modal Footer</template>
        </LargeModal>
      </div>

      <div class="bg-gray-200 px-6 lg:px-24 rounded p-6 w-full">
        <PageTabGroup>
          <PageTabList>
            <PageTab label="Tab One" />
            <PageTab label="Tab Two" />
            <PageTab label="Tab Three" />
          </PageTabList>
          <PageTabPanels>
            <PageTabPanel> Content One </PageTabPanel>
            <PageTabPanel> Content Two </PageTabPanel>
            <PageTabPanel> Content Three </PageTabPanel>
          </PageTabPanels>
        </PageTabGroup>
      </div>

      <div class="bg-gray-200 px-6 lg:px-24 rounded p-6 w-full flex flex-col gap-y-6">
        <FlagStatus :flags="['flag 1', 'flag 2', 'flag 3']" />
        <FlagStatus :flags="['flag 1']" />
      </div>
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref } from 'vue'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head } from '@inertiajs/vue3'
import BasicBadge from '@/Components/Badges/BasicBadge.vue'
import defaultTailwindConfig from 'tailwindcss/defaultTheme'
import tailwindConfig from '@/../../tailwind.config.js'
import MultiSelectInput from '@/Components/Forms/MultiSelectInput.vue'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { PageTabGroup, PageTabList, PageTab, PageTabPanels, PageTabPanel } from '@/Components/PageTabs'
import MediumModal from '@/Components/Modals/MediumModal.vue'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import LargeModal from '@/Components/Modals/LargeModal.vue'
import KitchenSink from '@/Components/KitchenSink.vue'
import FlagStatus from '@/Components/Flags/FlagStatus.vue'
const { show: showConfirmDialog } = useConfirmDialog()

console.log(defaultTailwindConfig)

const fontSizes = ['6xl', '5xl', '4xl', '3xl', '2xl', 'xl', 'lg', 'base', 'sm', 'xs', 'xxs']

const basicBadge = {
  colors: ['gray', 'red', 'blue', 'green'],
  sizes: ['large', 'default', 'small']
}

const multiSelectModel = ref([])

function reverseObjectProperties(obj) {
  const reversedObj = {}
  const keys = Object.keys(obj).reverse()

  keys.forEach((key) => {
    reversedObj[key] = obj[key]
  })

  return reversedObj
}

function fontSizeToPx(size) {
  let val = defaultTailwindConfig.fontSize[size] ?? tailwindConfig.theme.extend.fontSize[size]
  return remToPx(val[0])
}

function remToPx(remValue, baseFontSize = 16) {
  const rem = parseFloat(remValue)
  return `${rem * baseFontSize}px`
}

function getColor(cat, name = 'DEFAULT') {
  if (name !== 'DEFAULT') {
    return `bg-${cat}-${name}`
  }
  return `bg-${cat}`
}

async function openInfoConfirm() {
  try {
    await showConfirmDialog({
      type: 'info',
      title: 'Confirm Information',
      body: 'Are you sure you want to continue?',
      confirmText: 'Continue'
    })
  } catch (e) {
    console.log('User cancelled')
  }
}

async function openDangerConfirm() {
  try {
    await showConfirmDialog({
      type: 'danger',
      title: 'Confirm Danger',
      body: 'Are you sure you want to continue?',
      confirmText: 'Continue',
      cancelText: ''
    })
  } catch (e) {
    console.log('User cancelled')
  }
}

const mediumModalOpen = ref(false)
const openMediumModal = () => {
  mediumModalOpen.value = true
}
const closeMediumModal = () => {
  mediumModalOpen.value = false
}

const smallModalOpen = ref(false)
const openSmallModal = () => {
  smallModalOpen.value = true
}
const closeSmallModal = () => {
  smallModalOpen.value = false
}

const largeModalOpen = ref(false)
const openLargeModal = () => {
  largeModalOpen.value = true
}
const closeLargeModal = () => {
  largeModalOpen.value = false
}
</script>
